import { cn, truncateAddress } from "@gotts.ai/ui";
import { useEffect, useRef, useState } from "react";

import { TOKEN_SYMBOLS } from "../lib/constants";

import { TokenIcon } from "./TokenIcon";

import type { TokenSymbol } from "../lib/constants";
import type { Address } from "viem";

interface TokenSelectorProps {
  symbol: TokenSymbol;
  onChange: (s: TokenSymbol) => void;
  testId: string;
  /** Token address map for display in dropdown */
  tokenAddresses?: Record<string, Address>;
  /** Balance map for showing balances in dropdown */
  balances?: Record<string, { formatted: string }>;
  disabled?: boolean;
}

export function TokenSelector({
  symbol,
  onChange,
  testId,
  tokenAddresses,
  balances,
  disabled,
}: TokenSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => {
          if (!disabled) setOpen((v) => !v);
        }}
        className={cn(
          "octa-sm flex items-center gap-1.5 px-3 py-2 font-sans text-sm font-medium text-white transition-colors",
          "bg-[#1e2430] hover:bg-[#2a3040]",
          disabled && "cursor-not-allowed opacity-50",
        )}
        data-testid={testId}
        disabled={disabled}
      >
        <TokenIcon symbol={symbol} size={24} />
        <span>{symbol}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className="text-white/40"
        >
          <path
            d="M2 4 L5 7 L8 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[200px] overflow-hidden rounded-sm border border-[var(--glass-border)] bg-[#1a1e26] p-1 shadow-lg">
          {TOKEN_SYMBOLS.map((s) => {
            const addr = tokenAddresses?.[s.toLowerCase()];
            return (
              <button
                key={s}
                type="button"
                onClick={() => {
                  onChange(s);
                  setOpen(false);
                }}
                data-testid={`${testId}-option-${s}`}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left font-sans text-xs transition-colors",
                  s === symbol
                    ? "text-[var(--color-gold)]"
                    : "text-white/70 hover:bg-white/5 hover:text-white",
                )}
              >
                <TokenIcon symbol={s} size={20} />
                <div className="flex flex-1 flex-col">
                  <span className="font-medium">{s}</span>
                  {addr && (
                    <span className="text-[10px] text-white/30">
                      {truncateAddress(addr)}
                    </span>
                  )}
                </div>
                {balances?.[s] && (
                  <span className="font-mono text-[10px] text-white/30">
                    {balances[s].formatted}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
